<template>
  <div class="hris-page">
    <div class="hris-page-inner">
      <div class="hris-page-header">
        <span class="hris-page-eyebrow">Human Resources</span>
        <h1 class="hris-page-title">Performance Management</h1>
        <p class="hris-page-subtitle">Track KPIs, reviews, goals, development plans, and feedback.</p>
      </div>

      <div class="hris-panel" style="padding: 0; margin-bottom: 20px; overflow: hidden;">
        <q-tabs
          v-model="tab"
          active-color="green"
          indicator-color="green"
          align="left"
          inline-label
        >
          <q-tab name="kpis" label="KPIs" icon="fas fa-bullseye" />
          <q-tab name="reviews" label="Performance Reviews" icon="fas fa-clipboard-check" />
          <q-tab name="goals" label="Goals" icon="fas fa-flag-checkered" />
          <q-tab name="plans" label="Development Plans" icon="fas fa-seedling" />
          <q-tab name="feedback" label="Feedback" icon="fas fa-comments" />
        </q-tabs>
      </div>

      <q-tab-panels v-model="tab" animated style="background: transparent" class="q-pa-none">
        <!-- KPIs TAB -->
        <q-tab-panel name="kpis" class="q-pa-none">
          <div class="hris-toolbar">
            <q-btn v-if="isAdmin" unelevated class="hris-q-primary" icon="fas fa-plus" label="Add KPI" @click="openKpiDialog(null)" />
            <q-btn unelevated class="hris-q-outline" icon="fas fa-sync" label="Refresh" @click="getKPIs" />
          </div>
          <div v-if="!loadingKpis && kpis.length === 0" class="hris-panel">
            <div class="hris-empty">No KPIs found.</div>
          </div>
          <div v-else class="hris-grid">
            <div v-for="kpi in kpis" :key="kpi.id" class="hris-content-card">
              <div class="hris-content-card-body">
                <div class="row items-center justify-between" style="gap: 8px;">
                  <div class="hris-content-card-title">{{ kpi.kpi_name }}</div>
                  <q-chip dense color="blue-grey" text-color="white">{{ kpi.period }}</q-chip>
                </div>
                <div v-if="isAdmin && kpi.employee" class="hris-content-card-meta">
                  {{ kpi.employee.firstname }} {{ kpi.employee.lastname }}
                </div>
                <p class="hris-content-card-text">{{ kpi.description || 'No description.' }}</p>
                <div class="hris-content-card-meta">
                  Progress: {{ kpi.actual_value }} / {{ kpi.target_value }} &middot; Weight {{ kpi.weight }}%
                </div>
                <q-linear-progress
                  :value="kpiProgress(kpi)"
                  :color="kpiProgress(kpi) >= 1 ? 'positive' : 'green'"
                  size="14px"
                  rounded
                  class="q-mt-xs"
                />
              </div>
              <div class="hris-content-card-footer">
                <q-btn flat dense class="hris-q-ghost" icon="fas fa-chart-line" label="Log" @click="openKpiProgress(kpi)" />
                <q-btn v-if="isAdmin" flat round dense class="hris-q-ghost" icon="fas fa-edit" @click="openKpiDialog(kpi)">
                  <q-tooltip>Edit</q-tooltip>
                </q-btn>
                <q-btn v-if="isAdmin" flat round dense class="hris-q-ghost" icon="fas fa-trash-alt" @click="confirmDelete('kpis', kpi.id, kpi.kpi_name, getKPIs)">
                  <q-tooltip>Delete</q-tooltip>
                </q-btn>
              </div>
            </div>
          </div>
        </q-tab-panel>

        <!-- REVIEWS TAB -->
        <q-tab-panel name="reviews" class="q-pa-none">
          <div class="hris-toolbar">
            <q-btn v-if="isAdmin" unelevated class="hris-q-primary" icon="fas fa-plus" label="New Review" @click="openReviewForm(null)" />
            <q-btn unelevated class="hris-q-outline" icon="fas fa-sync" label="Refresh" @click="getReviews" />
          </div>
          <div class="hris-panel">
            <q-table
              flat
              :data="reviews"
              :columns="reviewColumns"
              row-key="id"
              :loading="loadingReviews"
              separator="cell"
            >
              <template v-slot:loading>
                <div class="hris-loading absolute-full flex flex-center" style="background: rgba(255,255,255,0.7); z-index: 1;">
                  <q-spinner color="green" size="40px" />
                  <div>Loading performance reviews...</div>
                </div>
              </template>
              <template v-slot:no-data>
                <div class="hris-empty full-width">No performance reviews found.</div>
              </template>
              <template v-slot:body="props">
                <q-tr :props="props">
                  <q-td key="employee" :props="props">
                    {{ props.row.employee ? props.row.employee.firstname + ' ' + props.row.employee.lastname : 'N/A' }}
                  </q-td>
                  <q-td key="review_period" :props="props">{{ props.row.review_period }}</q-td>
                  <q-td key="reviewer" :props="props">
                    {{ props.row.reviewer ? props.row.reviewer.firstname + ' ' + props.row.reviewer.lastname : 'N/A' }}
                  </q-td>
                  <q-td key="overall_rating" :props="props">
                    <q-rating v-if="props.row.overall_rating" :value="Number(props.row.overall_rating)" max="5" readonly size="1.2em" color="amber" />
                    <span v-else>—</span>
                  </q-td>
                  <q-td key="review_date" :props="props">{{ formatDate(props.row.review_date) }}</q-td>
                  <q-td key="status" :props="props">
                    <q-chip :color="reviewStatusColor(props.row.status)" text-color="white" dense>
                      {{ props.row.status.toUpperCase().replace('_', ' ') }}
                    </q-chip>
                  </q-td>
                  <q-td key="actions" :props="props" class="text-center">
                    <q-btn flat round dense class="hris-q-ghost" icon="fas fa-eye" @click="openReviewForm(props.row)">
                      <q-tooltip>{{ isAdmin ? 'Edit' : 'View' }}</q-tooltip>
                    </q-btn>
                    <q-btn v-if="isAdmin" flat round dense class="hris-q-ghost" icon="fas fa-trash-alt" @click="confirmDelete('reviews', props.row.id, 'this review', getReviews)">
                      <q-tooltip>Delete</q-tooltip>
                    </q-btn>
                  </q-td>
                </q-tr>
              </template>
            </q-table>
          </div>
        </q-tab-panel>

        <!-- GOALS TAB -->
        <q-tab-panel name="goals" class="q-pa-none">
          <div class="hris-toolbar">
            <q-btn unelevated class="hris-q-primary" icon="fas fa-plus" label="Add Goal" @click="openGoalDialog(null)" />
            <q-btn unelevated class="hris-q-outline" icon="fas fa-sync" label="Refresh" @click="getGoals" />
          </div>
          <div class="hris-panel">
            <q-table
              flat
              :data="goals"
              :columns="goalColumns"
              row-key="id"
              :loading="loadingGoals"
              separator="cell"
            >
              <template v-slot:loading>
                <div class="hris-loading absolute-full flex flex-center" style="background: rgba(255,255,255,0.7); z-index: 1;">
                  <q-spinner color="green" size="40px" />
                  <div>Loading goals...</div>
                </div>
              </template>
              <template v-slot:no-data>
                <div class="hris-empty full-width">No goals found.</div>
              </template>
              <template v-slot:body="props">
                <q-tr :props="props">
                  <q-td v-if="isAdmin" key="employee" :props="props">
                    {{ props.row.employee ? props.row.employee.firstname + ' ' + props.row.employee.lastname : 'N/A' }}
                  </q-td>
                  <q-td key="goal_title" :props="props">{{ props.row.goal_title }}</q-td>
                  <q-td key="due_date" :props="props">{{ formatDate(props.row.due_date) }}</q-td>
                  <q-td key="progress_pct" :props="props" style="min-width: 160px">
                    <q-linear-progress :value="props.row.progress_pct / 100" color="green" size="12px" rounded class="q-mb-xs" />
                    {{ props.row.progress_pct }}%
                  </q-td>
                  <q-td key="status" :props="props">
                    <q-chip :color="goalStatusColor(props.row.status)" text-color="white" dense>
                      {{ props.row.status.toUpperCase().replace('_', ' ') }}
                    </q-chip>
                  </q-td>
                  <q-td key="actions" :props="props" class="text-center">
                    <q-btn flat round dense class="hris-q-ghost" icon="fas fa-edit" @click="openGoalDialog(props.row)">
                      <q-tooltip>Edit</q-tooltip>
                    </q-btn>
                    <q-btn flat round dense class="hris-q-ghost" icon="fas fa-trash-alt" @click="confirmDelete('goals', props.row.id, props.row.goal_title, getGoals)">
                      <q-tooltip>Delete</q-tooltip>
                    </q-btn>
                  </q-td>
                </q-tr>
              </template>
            </q-table>
          </div>
        </q-tab-panel>

        <!-- DEVELOPMENT PLANS TAB -->
        <q-tab-panel name="plans" class="q-pa-none">
          <div class="hris-toolbar">
            <q-btn v-if="isAdmin" unelevated class="hris-q-primary" icon="fas fa-plus" label="Add Plan" @click="openPlanDialog(null)" />
            <q-btn unelevated class="hris-q-outline" icon="fas fa-sync" label="Refresh" @click="getPlans" />
          </div>
          <div v-if="!loadingPlans && plans.length === 0" class="hris-panel">
            <div class="hris-empty">No development plans found.</div>
          </div>
          <div v-else class="hris-grid">
            <div v-for="plan in plans" :key="plan.id" class="hris-content-card">
              <div class="hris-content-card-body">
                <div class="row items-center justify-between" style="gap: 8px;">
                  <div class="hris-content-card-title">{{ plan.area_of_improvement }}</div>
                  <q-chip :color="planStatusColor(plan.status)" text-color="white" dense>
                    {{ plan.status.toUpperCase().replace('_', ' ') }}
                  </q-chip>
                </div>
                <div v-if="isAdmin && plan.employee" class="hris-content-card-meta">
                  {{ plan.employee.firstname }} {{ plan.employee.lastname }}
                </div>
                <div class="hris-content-card-meta" style="font-weight: 600;">Action Steps</div>
                <p class="hris-content-card-text">{{ plan.action_steps }}</p>
                <div class="hris-content-card-meta"><b>Timeline:</b> {{ plan.timeline }}</div>
                <div v-if="plan.success_criteria" class="hris-content-card-meta"><b>Success Criteria:</b> {{ plan.success_criteria }}</div>
              </div>
              <div class="hris-content-card-footer">
                <q-btn flat dense class="hris-q-ghost" icon="fas fa-edit" label="Edit" @click="openPlanDialog(plan)" />
                <q-btn v-if="isAdmin" flat round dense class="hris-q-ghost" icon="fas fa-trash-alt" @click="confirmDelete('development-plans', plan.id, plan.area_of_improvement, getPlans)">
                  <q-tooltip>Delete</q-tooltip>
                </q-btn>
              </div>
            </div>
          </div>
        </q-tab-panel>

        <!-- FEEDBACK TAB -->
        <q-tab-panel name="feedback" class="q-pa-none">
          <div class="hris-toolbar">
            <q-btn unelevated class="hris-q-primary" icon="fas fa-plus" label="Give Feedback" @click="openFeedbackDialog" />
            <q-btn unelevated class="hris-q-outline" icon="fas fa-sync" label="Refresh" @click="getFeedback" />
          </div>
          <div v-if="!loadingFeedback && feedback.length === 0" class="hris-panel">
            <div class="hris-empty">No feedback found.</div>
          </div>
          <div v-else class="hris-grid">
            <div v-for="fb in feedback" :key="fb.id" class="hris-content-card">
              <div class="hris-content-card-body">
                <div class="row items-center justify-between" style="gap: 8px;">
                  <div class="hris-content-card-title">
                    <i class="fas fa-user-circle q-mr-xs"></i>
                    <span v-if="fb.is_anonymous && !isAdmin">Anonymous</span>
                    <span v-else-if="fb.provider">{{ fb.provider.firstname }} {{ fb.provider.lastname }}</span>
                    <span v-else>Unknown</span>
                    <q-chip v-if="fb.is_anonymous" dense color="grey" text-color="white" size="sm" class="q-ml-sm">Anonymous</q-chip>
                  </div>
                </div>
                <div v-if="isAdmin && fb.employee" class="hris-content-card-meta">
                  To: {{ fb.employee.firstname }} {{ fb.employee.lastname }}
                </div>
                <p class="hris-content-card-text">{{ fb.feedback_text }}</p>
              </div>
              <div v-if="isAdmin" class="hris-content-card-footer">
                <q-btn flat round dense class="hris-q-ghost" icon="fas fa-trash-alt" @click="confirmDelete('feedback', fb.id, 'this feedback', getFeedback)">
                  <q-tooltip>Delete</q-tooltip>
                </q-btn>
              </div>
            </div>
          </div>
        </q-tab-panel>
      </q-tab-panels>

      <!-- KPI DIALOG -->
      <q-dialog v-model="kpiDialog" persistent transition-show="fade" transition-hide="fade">
        <div class="hris-modal">
          <div class="hris-modal-header">
            <div>
              <h2 class="hris-modal-title">{{ kpiForm.id ? 'Edit KPI' : 'Add KPI' }}</h2>
            </div>
            <q-btn flat dense round v-close-popup class="hris-q-ghost" aria-label="Close">
              <i class="fas fa-times"></i>
            </q-btn>
          </div>
          <q-form @submit="saveKpi">
            <div class="hris-modal-body">
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-select
                    v-model="kpiForm.employee_id"
                    label="Employee"
                    :options="employeeOptions"
                    option-value="id"
                    :option-label="employeeLabel"
                    emit-value
                    map-options
                    use-input
                    outlined
                    dense
                    color="green"
                    @filter="filterEmployees"
                    :rules="[(val) => !!val || 'Employee is required']"
                  ></q-select>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input v-model="kpiForm.kpi_name" label="KPI Name" outlined dense color="green" :rules="[(val) => !!val || 'Required']"></q-input>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input v-model="kpiForm.description" label="Description" type="textarea" outlined dense color="green"></q-input>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field">
                  <q-input class="col" v-model.number="kpiForm.target_value" label="Target" type="number" step="0.01" outlined dense color="green" :rules="[(val) => val !== null && val !== '' || 'Required']"></q-input>
                </div>
                <div class="hris-form-field">
                  <q-input class="col" v-model.number="kpiForm.actual_value" label="Actual" type="number" step="0.01" outlined dense color="green"></q-input>
                </div>
                <div class="hris-form-field">
                  <q-input class="col" v-model.number="kpiForm.weight" label="Weight %" type="number" step="0.01" outlined dense color="green" :rules="[(val) => val !== null && val !== '' || 'Required']"></q-input>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input v-model="kpiForm.period" label="Period (e.g. Q3 2026)" outlined dense color="green" :rules="[(val) => !!val || 'Required']"></q-input>
                </div>
              </div>
            </div>
            <div class="hris-modal-footer">
              <q-btn unelevated class="hris-q-outline" label="Cancel" v-close-popup />
              <q-btn unelevated class="hris-q-primary" label="Save" type="submit" :loading="saving" />
            </div>
          </q-form>
        </div>
      </q-dialog>

      <!-- KPI PROGRESS DIALOG -->
      <q-dialog v-model="kpiProgressDialog" persistent transition-show="fade" transition-hide="fade">
        <div class="hris-modal">
          <div class="hris-modal-header">
            <div>
              <h2 class="hris-modal-title">Log Progress</h2>
            </div>
            <q-btn flat dense round v-close-popup class="hris-q-ghost" aria-label="Close">
              <i class="fas fa-times"></i>
            </q-btn>
          </div>
          <q-form @submit="saveKpiProgress">
            <div class="hris-modal-body">
              <div class="hris-content-card-meta">Target: {{ kpiProgressForm.target_value }}</div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input v-model.number="kpiProgressForm.actual_value" label="Actual Value" type="number" step="0.01" outlined dense color="green" :rules="[(val) => val !== null && val !== '' || 'Required']"></q-input>
                </div>
              </div>
            </div>
            <div class="hris-modal-footer">
              <q-btn unelevated class="hris-q-outline" label="Cancel" v-close-popup />
              <q-btn unelevated class="hris-q-primary" label="Save" type="submit" :loading="saving" />
            </div>
          </q-form>
        </div>
      </q-dialog>

      <!-- GOAL DIALOG -->
      <q-dialog v-model="goalDialog" persistent transition-show="fade" transition-hide="fade">
        <div class="hris-modal">
          <div class="hris-modal-header">
            <div>
              <h2 class="hris-modal-title">{{ goalForm.id ? 'Edit Goal' : 'Add Goal' }}</h2>
            </div>
            <q-btn flat dense round v-close-popup class="hris-q-ghost" aria-label="Close">
              <i class="fas fa-times"></i>
            </q-btn>
          </div>
          <q-form @submit="saveGoal">
            <div class="hris-modal-body">
              <div v-if="isAdmin" class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-select
                    v-model="goalForm.employee_id"
                    label="Employee"
                    :options="employeeOptions"
                    option-value="id"
                    :option-label="employeeLabel"
                    emit-value
                    map-options
                    use-input
                    outlined
                    dense
                    color="green"
                    @filter="filterEmployees"
                  ></q-select>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input v-model="goalForm.goal_title" label="Goal Title" outlined dense color="green" :rules="[(val) => !!val || 'Required']"></q-input>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input v-model="goalForm.description" label="Description" type="textarea" outlined dense color="green"></q-input>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field">
                  <q-input v-model="goalForm.start_date" label="Start Date" outlined dense color="green" mask="####-##-##" placeholder="YYYY-MM-DD" :rules="[(val) => !!val || 'Required']">
                    <template v-slot:append>
                      <i class="far fa-calendar-alt cursor-pointer">
                        <q-popup-proxy transition-show="scale" transition-hide="scale">
                          <q-date v-model="goalForm.start_date" mask="YYYY-MM-DD" />
                        </q-popup-proxy>
                      </i>
                    </template>
                  </q-input>
                </div>
                <div class="hris-form-field">
                  <q-input v-model="goalForm.due_date" label="Due Date" outlined dense color="green" mask="####-##-##" placeholder="YYYY-MM-DD" :rules="[(val) => !!val || 'Required']">
                    <template v-slot:append>
                      <i class="far fa-calendar-alt cursor-pointer">
                        <q-popup-proxy transition-show="scale" transition-hide="scale">
                          <q-date v-model="goalForm.due_date" mask="YYYY-MM-DD" />
                        </q-popup-proxy>
                      </i>
                    </template>
                  </q-input>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-select v-model="goalForm.status" label="Status" :options="goalStatusOptions" outlined dense color="green"></q-select>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-slider v-model="goalForm.progress_pct" :min="0" :max="100" label label-always color="green"></q-slider>
                </div>
              </div>
            </div>
            <div class="hris-modal-footer">
              <q-btn unelevated class="hris-q-outline" label="Cancel" v-close-popup />
              <q-btn unelevated class="hris-q-primary" label="Save" type="submit" :loading="saving" />
            </div>
          </q-form>
        </div>
      </q-dialog>

      <!-- PLAN DIALOG -->
      <q-dialog v-model="planDialog" persistent transition-show="fade" transition-hide="fade">
        <div class="hris-modal">
          <div class="hris-modal-header">
            <div>
              <h2 class="hris-modal-title">{{ planForm.id ? 'Edit Development Plan' : 'Add Development Plan' }}</h2>
            </div>
            <q-btn flat dense round v-close-popup class="hris-q-ghost" aria-label="Close">
              <i class="fas fa-times"></i>
            </q-btn>
          </div>
          <q-form @submit="savePlan">
            <div class="hris-modal-body">
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-select
                    v-model="planForm.employee_id"
                    label="Employee"
                    :options="employeeOptions"
                    option-value="id"
                    :option-label="employeeLabel"
                    emit-value
                    map-options
                    use-input
                    outlined
                    dense
                    color="green"
                    :disable="!!planForm.id && !isAdmin"
                    @filter="filterEmployees"
                    :rules="[(val) => !!val || 'Employee is required']"
                  ></q-select>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input v-model="planForm.area_of_improvement" label="Area of Improvement" outlined dense color="green" :rules="[(val) => !!val || 'Required']"></q-input>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input v-model="planForm.action_steps" label="Action Steps" type="textarea" outlined dense color="green" :rules="[(val) => !!val || 'Required']"></q-input>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input v-model="planForm.timeline" label="Timeline (e.g. 3 months)" outlined dense color="green" :rules="[(val) => !!val || 'Required']"></q-input>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input v-model="planForm.success_criteria" label="Success Criteria" type="textarea" outlined dense color="green"></q-input>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-select v-model="planForm.status" label="Status" :options="planStatusOptions" outlined dense color="green"></q-select>
                </div>
              </div>
            </div>
            <div class="hris-modal-footer">
              <q-btn unelevated class="hris-q-outline" label="Cancel" v-close-popup />
              <q-btn unelevated class="hris-q-primary" label="Save" type="submit" :loading="saving" />
            </div>
          </q-form>
        </div>
      </q-dialog>

      <!-- FEEDBACK DIALOG -->
      <q-dialog v-model="feedbackDialog" persistent transition-show="fade" transition-hide="fade">
        <div class="hris-modal">
          <div class="hris-modal-header">
            <div>
              <h2 class="hris-modal-title">Give Feedback</h2>
            </div>
            <q-btn flat dense round v-close-popup class="hris-q-ghost" aria-label="Close">
              <i class="fas fa-times"></i>
            </q-btn>
          </div>
          <q-form @submit="saveFeedback">
            <div class="hris-modal-body">
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-select
                    v-model="feedbackForm.employee_id"
                    label="Recipient"
                    :options="employeeOptions"
                    option-value="id"
                    :option-label="employeeLabel"
                    emit-value
                    map-options
                    use-input
                    outlined
                    dense
                    color="green"
                    @filter="filterEmployees"
                    :rules="[(val) => !!val || 'Recipient is required']"
                  ></q-select>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input v-model="feedbackForm.feedback_text" label="Feedback" type="textarea" outlined dense color="green" :rules="[(val) => !!val || 'Required']"></q-input>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-toggle v-model="feedbackForm.is_anonymous" label="Submit anonymously" color="green"></q-toggle>
                </div>
              </div>
            </div>
            <div class="hris-modal-footer">
              <q-btn unelevated class="hris-q-outline" label="Cancel" v-close-popup />
              <q-btn unelevated class="hris-q-primary" label="Submit" type="submit" :loading="saving" />
            </div>
          </q-form>
        </div>
      </q-dialog>

      <!-- REVIEW FORM DIALOG -->
      <review-form
        v-model="reviewFormOpen"
        :review="activeReview"
        :is-admin="isAdmin"
        :employee-options="employeeOptions"
        :current-user-id="userData.id"
        @filter-employees="filterEmployees"
        @saved="onReviewSaved"
      ></review-form>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { date } from "quasar";
import mixins from "../../mixins";
import ReviewForm from "./ReviewForm.vue";

export default {
  components: { ReviewForm },
  mixins: [mixins],
  data() {
    return {
      tab: "kpis",
      kpis: [],
      reviews: [],
      goals: [],
      plans: [],
      feedback: [],
      employeeOptions: [],

      loadingKpis: false,
      loadingReviews: false,
      loadingGoals: false,
      loadingPlans: false,
      loadingFeedback: false,
      saving: false,

      kpiDialog: false,
      kpiProgressDialog: false,
      goalDialog: false,
      planDialog: false,
      feedbackDialog: false,
      reviewFormOpen: false,
      activeReview: null,

      kpiForm: this.emptyKpi(),
      kpiProgressForm: { id: null, actual_value: 0, target_value: 0 },
      goalForm: this.emptyGoal(),
      planForm: this.emptyPlan(),
      feedbackForm: { employee_id: null, feedback_text: "", is_anonymous: false },

      goalStatusOptions: ["not_started", "in_progress", "completed", "deferred", "cancelled"],
      planStatusOptions: ["active", "completed", "on_hold"],

      reviewColumns: [
        { name: "employee", align: "left", label: "Employee", sortable: true },
        { name: "review_period", align: "left", label: "Period", sortable: true },
        { name: "reviewer", align: "left", label: "Reviewer" },
        { name: "overall_rating", align: "center", label: "Rating" },
        { name: "review_date", align: "center", label: "Date", sortable: true },
        { name: "status", align: "center", label: "Status", sortable: true },
        { name: "actions", align: "center", label: "Actions" }
      ]
    };
  },
  computed: {
    isAdmin() {
      const roleId = Number(this.userData.role_id);
      return roleId === 0 || roleId === 1;
    },
    goalColumns() {
      const cols = [];
      if (this.isAdmin) cols.push({ name: "employee", align: "left", label: "Employee", sortable: true });
      cols.push(
        { name: "goal_title", align: "left", label: "Goal", sortable: true },
        { name: "due_date", align: "center", label: "Due Date", sortable: true },
        { name: "progress_pct", align: "left", label: "Progress" },
        { name: "status", align: "center", label: "Status", sortable: true },
        { name: "actions", align: "center", label: "Actions" }
      );
      return cols;
    }
  },
  created() {
    this.getKPIs();
    this.getReviews();
    this.getGoals();
    this.getPlans();
    this.getFeedback();
    this.getEmployees();
  },
  methods: {
    emptyKpi() {
      return { id: null, employee_id: null, kpi_name: "", description: "", target_value: null, actual_value: 0, weight: null, period: "" };
    },
    emptyGoal() {
      return { id: null, employee_id: null, goal_title: "", description: "", start_date: "", due_date: "", status: "not_started", progress_pct: 0 };
    },
    emptyPlan() {
      return { id: null, employee_id: null, area_of_improvement: "", action_steps: "", timeline: "", success_criteria: "", status: "active" };
    },
    getHeaders() {
      return { headers: { Authorization: "Bearer " + this.token } };
    },
    formatDate(d) {
      if (!d) return "N/A";
      return date.formatDate(d, "MMMM DD, YYYY");
    },
    employeeLabel(opt) {
      if (!opt) return "";
      return `${opt.firstname} ${opt.lastname}`;
    },
    kpiProgress(kpi) {
      const t = Number(kpi.target_value);
      if (!t) return 0;
      return Math.min(1, Number(kpi.actual_value) / t);
    },
    reviewStatusColor(s) {
      return { draft: "grey", under_review: "warning", acknowledged: "info", completed: "positive" }[s] || "grey";
    },
    goalStatusColor(s) {
      return { not_started: "grey", in_progress: "warning", completed: "positive", deferred: "info", cancelled: "negative" }[s] || "grey";
    },
    planStatusColor(s) {
      return { active: "primary", completed: "positive", on_hold: "warning" }[s] || "grey";
    },

    // --- fetchers ---
    fetchList(url, key, loadingKey) {
      this[loadingKey] = true;
      axios
        .get(this.apiUrl + url, this.getHeaders())
        .then((res) => {
          if (res.data.status) this[key] = res.data.data;
        })
        .finally(() => {
          this[loadingKey] = false;
        });
    },
    getKPIs() {
      this.fetchList("/performance/kpis", "kpis", "loadingKpis");
    },
    getReviews() {
      this.fetchList("/performance/reviews", "reviews", "loadingReviews");
    },
    getGoals() {
      this.fetchList("/performance/goals", "goals", "loadingGoals");
    },
    getPlans() {
      this.fetchList("/performance/development-plans", "plans", "loadingPlans");
    },
    getFeedback() {
      this.fetchList("/performance/feedback", "feedback", "loadingFeedback");
    },
    getEmployees(filter = "") {
      axios
        .get(this.apiUrl + "/employees", {
          headers: { Authorization: "Bearer " + this.token },
          params: { status: "ACTIVE", countPerPage: 20, start: 0, filter }
        })
        .then((res) => {
          if (res.data.status) this.employeeOptions = res.data.data || [];
        });
    },
    filterEmployees(val, update) {
      update(() => {
        this.getEmployees(val);
      });
    },

    // --- generic save/delete ---
    post(url, payload, onSuccess, successMsg) {
      this.saving = true;
      axios
        .post(this.apiUrl + url, payload, this.getHeaders())
        .then((res) => {
          if (res.data.status) {
            this.$q.notify({ type: "positive", message: successMsg });
            onSuccess();
          } else {
            this.$q.notify({ type: "negative", message: res.data.message || "Operation failed." });
          }
        })
        .catch(() => {
          this.$q.notify({ type: "negative", message: "Error communicating with server." });
        })
        .finally(() => {
          this.saving = false;
        });
    },
    confirmDelete(segment, id, label, refresh) {
      this.$q
        .dialog({ title: "Confirm Deletion", message: `Delete "${label}"?`, cancel: true, persistent: true })
        .onOk(() => {
          axios.post(this.apiUrl + `/performance/${segment}/delete/${id}`, {}, this.getHeaders()).then((res) => {
            if (res.data.status) {
              this.$q.notify({ type: "positive", message: "Deleted." });
              refresh();
            } else {
              this.$q.notify({ type: "negative", message: res.data.message || "Failed to delete." });
            }
          });
        });
    },

    // --- KPI ---
    openKpiDialog(kpi) {
      this.kpiForm = kpi
        ? { id: kpi.id, employee_id: kpi.employee_id, kpi_name: kpi.kpi_name, description: kpi.description || "", target_value: Number(kpi.target_value), actual_value: Number(kpi.actual_value), weight: Number(kpi.weight), period: kpi.period }
        : this.emptyKpi();
      this.kpiDialog = true;
    },
    saveKpi() {
      const url = this.kpiForm.id ? "/performance/kpis/update" : "/performance/kpis/create";
      this.post(url, this.kpiForm, () => {
        this.kpiDialog = false;
        this.getKPIs();
      }, `KPI ${this.kpiForm.id ? "updated" : "created"}.`);
    },
    openKpiProgress(kpi) {
      this.kpiProgressForm = { id: kpi.id, actual_value: Number(kpi.actual_value), target_value: Number(kpi.target_value) };
      this.kpiProgressDialog = true;
    },
    saveKpiProgress() {
      this.post("/performance/kpis/update", { id: this.kpiProgressForm.id, actual_value: this.kpiProgressForm.actual_value }, () => {
        this.kpiProgressDialog = false;
        this.getKPIs();
      }, "Progress logged.");
    },

    // --- GOAL ---
    openGoalDialog(goal) {
      this.goalForm = goal
        ? { id: goal.id, employee_id: goal.employee_id, goal_title: goal.goal_title, description: goal.description || "", start_date: goal.start_date, due_date: goal.due_date, status: goal.status, progress_pct: goal.progress_pct }
        : this.emptyGoal();
      this.goalDialog = true;
    },
    saveGoal() {
      const url = this.goalForm.id ? "/performance/goals/update" : "/performance/goals/create";
      this.post(url, this.goalForm, () => {
        this.goalDialog = false;
        this.getGoals();
      }, `Goal ${this.goalForm.id ? "updated" : "created"}.`);
    },

    // --- PLAN ---
    openPlanDialog(plan) {
      this.planForm = plan
        ? { id: plan.id, employee_id: plan.employee_id, area_of_improvement: plan.area_of_improvement, action_steps: plan.action_steps, timeline: plan.timeline, success_criteria: plan.success_criteria || "", status: plan.status }
        : this.emptyPlan();
      this.planDialog = true;
    },
    savePlan() {
      const url = this.planForm.id ? "/performance/development-plans/update" : "/performance/development-plans/create";
      this.post(url, this.planForm, () => {
        this.planDialog = false;
        this.getPlans();
      }, `Development plan ${this.planForm.id ? "updated" : "created"}.`);
    },

    // --- FEEDBACK ---
    openFeedbackDialog() {
      this.feedbackForm = { employee_id: null, feedback_text: "", is_anonymous: false };
      this.feedbackDialog = true;
    },
    saveFeedback() {
      this.post("/performance/feedback/submit", this.feedbackForm, () => {
        this.feedbackDialog = false;
        this.getFeedback();
      }, "Feedback submitted.");
    },

    // --- REVIEW ---
    openReviewForm(review) {
      this.activeReview = review;
      this.reviewFormOpen = true;
    },
    onReviewSaved() {
      this.reviewFormOpen = false;
      this.getReviews();
    }
  }
};
</script>
